/**
 * Alert — canonical Henaket page-top status banner.
 *
 * Ship 0.36.462. Horizontal component with a colored left bar +
 * light-tint background. Distinct from the Error Summary rectangle
 * (aggregated form errors with anchor links); Alert is a lighter-
 * touch status banner used for save-succeeded messages, warnings
 * about state, or a single-line error.
 *
 * Variants
 * --------
 *   'info'    (default) — blue: informational, orientation, hints
 *   'success' — green:  operation completed, saved, etc.
 *   'warning' — amber:  attention needed, non-blocking issue
 *   'error'   — red:    single-line error (for aggregated form
 *                        errors, use <ErrorSummary /> instead)
 *
 * Props
 * -----
 *   variant     — one of 'info' | 'success' | 'warning' | 'error'
 *   title       — optional heading (semibold)
 *   message     — body message
 *   children    — alternative to `message` for richer content
 *   onClose     — optional close callback; renders an × button
 *   role        — ARIA role, defaults to 'alert' for error/warning,
 *                  'status' for info/success. Override to force.
 *
 * Examples
 * --------
 *   <Alert variant="success" message="Settings saved." />
 *
 *   <Alert
 *     variant="warning"
 *     title="This will affect all users"
 *     message="Changing signup mode reroutes any pending invites."
 *   />
 *
 *   <Alert
 *     variant="info"
 *     title="Read-only mode"
 *     message="You have viewer access; contact your admin to edit."
 *     onClose={() => setDismissed(true)}
 *   />
 */
export default function Alert({
  variant = 'info',
  title,
  message,
  children,
  onClose,
  role,
}) {
  const cls = `henaket-alert henaket-alert-${variant}`;
  // Assertive ARIA role for issues that need attention, polite for
  // status updates. Caller can override with the `role` prop.
  const effectiveRole = role || (
    variant === 'error' || variant === 'warning' ? 'alert' : 'status'
  );
  return (
    <div className={cls} role={effectiveRole}>
      <div className="henaket-alert-bar" aria-hidden="true" />
      <div className="henaket-alert-content">
        {title && <div className="henaket-alert-heading">{title}</div>}
        {message ? (
          <div className="henaket-alert-body">{message}</div>
        ) : children}
      </div>
      {onClose && (
        <button
          type="button"
          className="henaket-alert-close"
          onClick={onClose}
          aria-label="Close"
        >
          {/* 0.36.560 → 0.36.561 — Full mirror of ConfirmDialog's
              .topClose: 24×24 SVG × followed by the underlined
              "Close" text. Same visual language across every
              dismissible surface in the app. */}
          <svg
            className="henaket-alert-close-icon"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M5 5 L19 19 M19 5 L5 19"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <span className="henaket-alert-close-text">Close</span>
        </button>
      )}
    </div>
  );
}
